/* AISEN FLOOR — motor de cálculo (configuração + funções puras).
   Sem dependências, sem DOM. Exposto em window.AisenFloorEngine para a UI e para os testes.

   Regras comerciais AISEN (não alterar sem validação):
   - 150 W/m² nominal, faixa aceita de 135 a 165 W/m²
   - 1 ambiente = 1 zona = 1 termostato = 1 caixa 86 × 86 mm
   - até 16 A: acionamento direto | 16–25 A: termostato + contatora | acima de 25 A: análise técnica
   - o limite comercial é 25 A (os 32 A AC-1 do fabricante NÃO são usados)
   - a contatora de uma zona nunca considera a corrente de outras zonas
*/
(() => {
  const AISEN_FLOOR_CONFIG = {
    voltage: 220,

    nominalWattsPerSquareMeter: 150,
    minimumWattsPerSquareMeter: 135,
    maximumWattsPerSquareMeter: 165,

    thermostatMaximumCurrent: 16,
    contactorOperationalCurrent: 25,

    thermostatQuantityPerZone: 1,
    europeanBoxQuantityPerZone: 1,

    maximumEnvironmentCount: 20,
    maximumRollsPerZone: 16,   // teto de busca; nenhuma zona padrão chega perto disso
    maximumHeatedArea: 500,    // sanidade de entrada

    // ambientes compactos: quando nenhuma combinação cai em 135–165 W/m², recomenda 1 rolo padrão.
    // O limite é derivado do rolo seguinte (500 / 165), nunca escrito fixo.
    smallAreaFallback: {
      enabled: true,
      rollId: 'floor-300',
      calculateMaximumAreaFromNextRoll: true,
      maximumArea: null, // usado apenas se calculateMaximumAreaFromNextRoll = false
    },

    // Fita metálica: duas linhas paralelas por ambiente, ao longo do maior lado.
    // Unidade comercial de 3 m, sem fração e sem aproveitar sobra entre lados ou entre ambientes.
    metallicTape: {
      enabled: true,
      unitLengthMeters: 3,
      parallelLinesPerZone: 2,
      dimensionRule: 'longest_side',
    },
  };

  // variantId é preenchido pela section a partir das configurações do editor
  const AISEN_FLOOR_ROLLS = [
    { id: 'floor-300',  title: 'AISEN FLOOR 300 W',  powerWatts: 300,  lengthMeters: 15, variantId: null },
    { id: 'floor-500',  title: 'AISEN FLOOR 500 W',  powerWatts: 500,  lengthMeters: 25, variantId: null },
    { id: 'floor-700',  title: 'AISEN FLOOR 700 W',  powerWatts: 700,  lengthMeters: 35, variantId: null },
    { id: 'floor-900',  title: 'AISEN FLOOR 900 W',  powerWatts: 900,  lengthMeters: 45, variantId: null },
    { id: 'floor-1200', title: 'AISEN FLOOR 1200 W', powerWatts: 1200, lengthMeters: 60, variantId: null },
  ];

  const STATUS = {
    IDLE: 'idle',
    VALID: 'valid',
    NO_COMBINATION: 'no_standard_combination',
    TECHNICAL_REVIEW: 'technical_review',
    INVALID_INPUT: 'invalid_input',
  };

  const CONTROL = {
    DIRECT: 'direct_thermostat',
    CONTACTOR: 'thermostat_with_contactor',
    REVIEW: 'technical_review',
  };

  const SELECTION = {
    STANDARD: 'standard',
    SMALL_AREA_FALLBACK: 'small_area_fallback',
  };

  /* ---------------- fallback de área pequena ---------------- */

  // limite = potência do rolo imediatamente acima do rolo de fallback ÷ W/m² máximo.
  // Com o catálogo atual: 500 / 165 ≈ 3,0303 m². Recalcula sozinho se o catálogo mudar.
  function getSmallAreaFallbackMaximum(config, rolls) {
    const cfg = config || AISEN_FLOOR_CONFIG;
    const fb = cfg.smallAreaFallback;
    if (!fb || !fb.enabled) return 0;
    if (!fb.calculateMaximumAreaFromNextRoll) return fb.maximumArea || 0;

    const list = (rolls || AISEN_FLOOR_ROLLS).slice().sort((a, b) => a.powerWatts - b.powerWatts);
    const base = list.find((r) => r.id === fb.rollId);
    if (!base) return 0;
    const next = list.find((r) => r.powerWatts > base.powerWatts);
    if (!next) return 0;
    return next.powerWatts / cfg.maximumWattsPerSquareMeter;
  }

  function getSmallAreaFallbackRoll(config, rolls) {
    const cfg = config || AISEN_FLOOR_CONFIG;
    const fb = cfg.smallAreaFallback;
    if (!fb || !fb.enabled) return null;
    return (rolls || AISEN_FLOOR_ROLLS).find((r) => r.id === fb.rollId) || null;
  }

  /* ---------------- entrada ---------------- */

  // aceita "7,5" e "7.5"; devolve null quando não é número utilizável
  function normalizeAreaInput(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (value === null || value === undefined) return null;
    const raw = String(value).trim();
    if (!raw) return null;
    if (!/^-?\d+(?:[.,]\d+)?$/.test(raw.replace(/\s/g, ''))) return null;
    const n = parseFloat(raw.replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(n)) return null;
    return Math.round(n * 100) / 100; // 2 casas
  }

  // Aceita as duas formas: comprimento + largura (padrão da calculadora, necessário para a fita)
  // ou heatedArea direta (uso programático; sem dimensões a fita não é calculada).
  function validateEnvironment(environment, config) {
    const cfg = config || AISEN_FLOOR_CONFIG;
    const env = environment || {};
    const hasDims = env.lengthMeters !== undefined || env.widthMeters !== undefined;

    let area, length = null, width = null;
    if (hasDims) {
      length = normalizeAreaInput(env.lengthMeters);
      width = normalizeAreaInput(env.widthMeters);
      if (length === null) return { ok: false, code: 'invalid_length', field: 'length', message: 'Informe o comprimento em metros.' };
      if (length <= 0) return { ok: false, code: 'length_not_positive', field: 'length', message: 'O comprimento deve ser maior que zero.' };
      if (width === null) return { ok: false, code: 'invalid_width', field: 'width', message: 'Informe a largura em metros.' };
      if (width <= 0) return { ok: false, code: 'width_not_positive', field: 'width', message: 'A largura deve ser maior que zero.' };
      area = Math.round(length * width * 100) / 100;
    } else {
      area = normalizeAreaInput(env.heatedArea);
      if (area === null) return { ok: false, code: 'invalid_number', field: 'area', message: 'Informe a área aquecida em metros quadrados.' };
      if (area <= 0) return { ok: false, code: 'not_positive', field: 'area', message: 'A área aquecida deve ser maior que zero.' };
    }

    if (area > cfg.maximumHeatedArea) return { ok: false, code: 'too_large', field: 'area', message: 'Para áreas acima de ' + cfg.maximumHeatedArea + ' m², solicite uma análise técnica.' };
    return { ok: true, heatedArea: area, lengthMeters: length, widthMeters: width };
  }

  /* ---------------- fita metálica ---------------- */

  function calculateMetallicTape({ lengthMeters, widthMeters, config }) {
    const cfg = config || AISEN_FLOOR_CONFIG;
    const tape = cfg.metallicTape;
    if (!tape || !tape.enabled) return { enabled: false, quantity: 0 };
    if (!(lengthMeters > 0) || !(widthMeters > 0)) return { enabled: true, quantity: 0, missingDimensions: true };

    // regra atual: as duas linhas correm ao longo do maior lado
    const installationLengthMeters = tape.dimensionRule === 'longest_side'
      ? Math.max(lengthMeters, widthMeters)
      : Math.max(lengthMeters, widthMeters);

    // ceil obrigatório: a fita não é vendida fracionada nem compartilhada entre os lados
    const unitsPerLine = Math.ceil(installationLengthMeters / tape.unitLengthMeters);
    const quantity = unitsPerLine * tape.parallelLinesPerZone;

    return {
      enabled: true,
      unitLengthMeters: tape.unitLengthMeters,
      installationLengthMeters,
      parallelLines: tape.parallelLinesPerZone,
      unitsPerLine,
      quantity,
      totalSuppliedLengthMeters: quantity * tape.unitLengthMeters,
    };
  }

  /* ---------------- combinações ---------------- */

  // backtracking com poda: nunca ultrapassa maximumPower nem o teto de rolos
  function generateValidRollCombinations({ minimumPower, maximumPower, rolls, maximumRolls }) {
    const list = (rolls || []).slice().sort((a, b) => a.powerWatts - b.powerWatts);
    const capRolls = maximumRolls || AISEN_FLOOR_CONFIG.maximumRollsPerZone;
    const out = [];
    const counts = new Array(list.length).fill(0);

    const walk = (index, usedRolls, power) => {
      if (power > maximumPower) return;
      if (index === list.length) {
        if (usedRolls === 0 || power < minimumPower) return;
        out.push({
          rolls: list.map((r, i) => ({ roll: r, quantity: counts[i] })).filter((x) => x.quantity > 0),
          totalRolls: usedRolls,
          totalPower: power,
          distinctModels: counts.filter((c) => c > 0).length,
        });
        return;
      }
      const r = list[index];
      const maxQty = Math.min(capRolls - usedRolls, Math.floor((maximumPower - power) / r.powerWatts));
      for (let q = 0; q <= maxQty; q++) {
        counts[index] = q;
        walk(index + 1, usedRolls + q, power + q * r.powerWatts);
      }
      counts[index] = 0;
    };

    walk(0, 0, 0);
    return out;
  }

  // menor é melhor; a última chave garante resultado determinístico
  function scoreCombination({ combination, idealPower }) {
    return {
      powerDelta: Math.abs(combination.totalPower - idealPower),
      totalRolls: combination.totalRolls,
      distinctModels: combination.distinctModels,
      totalPower: combination.totalPower,
      signature: combination.rolls.map((x) => x.roll.id + ':' + x.quantity).join('|'),
    };
  }

  function findBestRollCombination({ heatedArea, rolls, config }) {
    const cfg = config || AISEN_FLOOR_CONFIG;
    const idealPower = heatedArea * cfg.nominalWattsPerSquareMeter;
    const minimumPower = heatedArea * cfg.minimumWattsPerSquareMeter;
    const maximumPower = heatedArea * cfg.maximumWattsPerSquareMeter;

    const combos = generateValidRollCombinations({
      minimumPower, maximumPower,
      rolls: rolls || AISEN_FLOOR_ROLLS,
      maximumRolls: cfg.maximumRollsPerZone,
    });
    if (!combos.length) return { found: false, idealPower, minimumPower, maximumPower };

    let best = null, bestScore = null;
    for (const c of combos) {
      const s = scoreCombination({ combination: c, idealPower });
      if (
        !bestScore ||
        s.powerDelta < bestScore.powerDelta - 1e-9 ||
        (Math.abs(s.powerDelta - bestScore.powerDelta) <= 1e-9 && (
          s.totalRolls < bestScore.totalRolls ||
          (s.totalRolls === bestScore.totalRolls && (
            s.distinctModels < bestScore.distinctModels ||
            (s.distinctModels === bestScore.distinctModels && (
              s.totalPower < bestScore.totalPower ||
              (s.totalPower === bestScore.totalPower && s.signature < bestScore.signature)
            ))
          ))
        ))
      ) { best = c; bestScore = s; }
    }
    return { found: true, combination: best, idealPower, minimumPower, maximumPower };
  }

  /* ---------------- comando elétrico ---------------- */

  function calculateElectricalControl({ installedPower, voltage, thermostatMaximumCurrent, contactorOperationalCurrent }) {
    const v = voltage || AISEN_FLOOR_CONFIG.voltage;
    const maxDirect = thermostatMaximumCurrent === undefined ? AISEN_FLOOR_CONFIG.thermostatMaximumCurrent : thermostatMaximumCurrent;
    const maxContactor = contactorOperationalCurrent === undefined ? AISEN_FLOOR_CONFIG.contactorOperationalCurrent : contactorOperationalCurrent;
    const current = installedPower / v;

    if (current <= maxDirect) {
      return { current, controlType: CONTROL.DIRECT, thermostatQuantity: 1, europeanBoxQuantity: 1, contactorQuantity: 0, requiresTechnicalReview: false };
    }
    if (current <= maxContactor) {
      return { current, controlType: CONTROL.CONTACTOR, thermostatQuantity: 1, europeanBoxQuantity: 1, contactorQuantity: 1, requiresTechnicalReview: false };
    }
    return { current, controlType: CONTROL.REVIEW, thermostatQuantity: 1, europeanBoxQuantity: 1, contactorQuantity: 0, requiresTechnicalReview: true };
  }

  /* ---------------- zona e projeto ---------------- */

  function calculateFloorZone(environment, options) {
    const opts = options || {};
    const cfg = opts.config || AISEN_FLOOR_CONFIG;
    const rolls = opts.rolls || AISEN_FLOOR_ROLLS;
    const zoneNumber = environment.zoneNumber || 1;
    const base = {
      zoneId: environment.zoneId || ('zone-' + zoneNumber),
      zoneNumber,
      environmentName: (environment.environmentName || '').trim() || ('Ambiente ' + zoneNumber),
      thermostatId: environment.thermostatId || null,
      heatedArea: null,
      status: STATUS.IDLE,
      selectedRolls: [],
      thermostatQuantity: 0,
      europeanBoxQuantity: 0,
      contactorQuantity: 0,
      requiresTechnicalReview: false,
    };

    const v = validateEnvironment(environment, cfg);
    if (!v.ok) return Object.assign(base, { status: STATUS.INVALID_INPUT, error: v });

    const area = v.heatedArea;
    const found = findBestRollCombination({ heatedArea: area, rolls, config: cfg });
    // a fita depende das dimensões, nunca só da área; calculada por zona, sem aproveitar sobra
    const tape = calculateMetallicTape({ lengthMeters: v.lengthMeters, widthMeters: v.widthMeters, config: cfg });
    const common = {
      heatedArea: area,
      lengthMeters: v.lengthMeters,
      widthMeters: v.widthMeters,
      metallicTape: tape,
      idealPower: found.idealPower,
      minimumPower: found.minimumPower,
      maximumPower: found.maximumPower,
    };

    // sem combinação padrão: ambientes compactos recebem 1 rolo de fallback em vez de análise técnica
    let combo = found.combination;
    let selectionType = SELECTION.STANDARD;

    if (!found.found) {
      const limit = getSmallAreaFallbackMaximum(cfg, rolls);
      const fbRoll = getSmallAreaFallbackRoll(cfg, rolls);
      if (fbRoll && limit > 0 && area <= limit + 1e-9) {
        combo = {
          rolls: [{ roll: fbRoll, quantity: 1 }],
          totalRolls: 1,
          totalPower: fbRoll.powerWatts,
          distinctModels: 1,
        };
        selectionType = SELECTION.SMALL_AREA_FALLBACK;
      } else {
        return Object.assign(base, common, { status: STATUS.NO_COMBINATION, selectionType: SELECTION.STANDARD, usesSmallAreaFallback: false });
      }
    }

    const installedPower = combo.totalPower;
    const installedLength = combo.rolls.reduce((s, x) => s + x.quantity * x.roll.lengthMeters, 0);
    const control = calculateElectricalControl({
      installedPower,
      voltage: cfg.voltage,
      thermostatMaximumCurrent: cfg.thermostatMaximumCurrent,
      contactorOperationalCurrent: cfg.contactorOperationalCurrent,
    });

    return Object.assign(base, common, {
      status: control.requiresTechnicalReview ? STATUS.TECHNICAL_REVIEW : STATUS.VALID,
      selectionType,
      usesSmallAreaFallback: selectionType === SELECTION.SMALL_AREA_FALLBACK,
      installedPower,
      installedWattsPerSquareMeter: installedPower / area,
      installedLength,
      installedCurrent: control.current,
      selectedRolls: combo.rolls.map((x) => ({
        id: x.roll.id, title: x.roll.title, quantity: x.quantity,
        powerWatts: x.roll.powerWatts, lengthMeters: x.roll.lengthMeters, variantId: x.roll.variantId,
      })),
      thermostatQuantity: control.thermostatQuantity,
      europeanBoxQuantity: control.europeanBoxQuantity,
      contactorQuantity: control.requiresTechnicalReview ? 0 : control.contactorQuantity,
      controlType: control.controlType,
      requiresTechnicalReview: control.requiresTechnicalReview,
    });
  }

  function calculateFloorProject(environments, options) {
    const opts = options || {};
    const zones = (environments || []).map((env, i) =>
      calculateFloorZone(Object.assign({}, env, { zoneNumber: env.zoneNumber || i + 1 }), opts));
    return consolidateFloorProject(zones, opts);
  }

  function consolidateFloorProject(zones, options) {
    const opts = options || {};
    const projectId = opts.projectId || ('AF-' + Math.random().toString(36).slice(2, 8).toUpperCase());
    const valid = zones.filter((z) => z.status === STATUS.VALID);
    const pending = zones.filter((z) => z.status !== STATUS.VALID);

    const rollTotals = {};
    valid.forEach((z) => z.selectedRolls.forEach((r) => {
      rollTotals[r.id] = (rollTotals[r.id] || 0) + r.quantity;
    }));

    const totalInstalledPower = valid.reduce((s, z) => s + z.installedPower, 0);
    return {
      projectId,
      zones,
      totals: {
        totalEnvironmentCount: zones.length,
        validEnvironmentCount: valid.length,
        pendingEnvironmentCount: pending.length,
        totalHeatedArea: valid.reduce((s, z) => s + z.heatedArea, 0),
        totalInstalledPower,
        totalInstalledLength: valid.reduce((s, z) => s + z.installedLength, 0),
        // informativo: nunca usado para decidir contatora de zona
        totalCurrentInformational: totalInstalledPower / (opts.config || AISEN_FLOOR_CONFIG).voltage,
        thermostatQuantity: valid.reduce((s, z) => s + z.thermostatQuantity, 0),
        europeanBoxQuantity: valid.reduce((s, z) => s + z.europeanBoxQuantity, 0),
        contactorQuantity: valid.reduce((s, z) => s + z.contactorQuantity, 0),
        // soma por zona: nenhuma sobra é reaproveitada entre ambientes
        metallicTapeQuantity: valid.reduce((s, z) => s + ((z.metallicTape && z.metallicTape.quantity) || 0), 0),
        metallicTapeTotalLengthMeters: valid.reduce((s, z) => s + ((z.metallicTape && z.metallicTape.totalSuppliedLengthMeters) || 0), 0),
        rollTotals,
      },
    };
  }

  /* ---------------- termostatos ---------------- */

  // catalog.thermostats = [{id, title, variantId}] · aceita catalog.thermostat (single) por compatibilidade
  function getThermostatOptions(catalog) {
    const cat = catalog || {};
    if (Array.isArray(cat.thermostats) && cat.thermostats.length) return cat.thermostats;
    if (cat.thermostat) return [{ id: 'thermostat', title: 'Termostato AISEN FLOOR', variantId: cat.thermostat }];
    return [{ id: 'thermostat', title: 'Termostato AISEN FLOOR', variantId: null }];
  }

  // aceita number (variantId puro) ou objeto { variantId, image }
  function auxVariantId(entry) { return entry && typeof entry === 'object' ? entry.variantId : (entry || null); }
  function auxImage(entry) { return entry && typeof entry === 'object' ? (entry.image || null) : null; }

  function resolveThermostat(catalog, thermostatId) {
    const options = getThermostatOptions(catalog);
    return options.find((o) => o.id === thermostatId) || options[0];
  }

  /* ---------------- materiais consolidados ---------------- */

  // Fonte única da verdade: alimenta a tabela do resumo E o carrinho.
  // Consolida apenas zonas com status "valid" (inclui o fallback de área pequena).
  function buildConsolidatedProjectMaterials(project, catalog, options) {
    const cat = catalog || {};
    const opts = options || {};
    const cfg = opts.config || AISEN_FLOOR_CONFIG;
    const rolls = opts.rolls || AISEN_FLOOR_ROLLS;
    const valid = project.zones.filter((z) => z.status === STATUS.VALID);

    const materials = [];

    // 1) cabos agrupados por SKU, em ordem crescente de potência
    const byRoll = {};
    valid.forEach((z) => z.selectedRolls.forEach((r) => {
      if (!byRoll[r.id]) byRoll[r.id] = { ref: r, quantity: 0 };
      byRoll[r.id].quantity += r.quantity;
    }));
    Object.keys(byRoll)
      .map((id) => byRoll[id])
      .sort((a, b) => a.ref.powerWatts - b.ref.powerWatts)
      .forEach((x, i) => {
        const cat_ = rolls.find((r) => r.id === x.ref.id);
        materials.push({
          type: 'cable',
          productId: x.ref.id,
          variantId: (cat_ && cat_.variantId) || x.ref.variantId || null,
          title: x.ref.title + ' — ' + x.ref.lengthMeters + ' m',
          description: 'Cabo de aquecimento para piso',
          specification: x.ref.lengthMeters + ' m por unidade',
          quantity: x.quantity,
          unit: 'un.',
          imageUrl: (cat_ && cat_.image) || x.ref.image || null,
          totalLabel: (x.quantity * x.ref.lengthMeters) + ' m',
          totalLengthMeters: x.quantity * x.ref.lengthMeters,
          sortOrder: 100 + i,
        });
      });

    // 2) termostatos — agrupados por modelo escolhido em cada ambiente
    const thermostatOptions = getThermostatOptions(cat);
    const byThermostat = {};
    valid.forEach((z) => {
      if (!z.thermostatQuantity) return;
      const opt = resolveThermostat(cat, z.thermostatId);
      const key = (opt && opt.id) || 'thermostat';
      if (!byThermostat[key]) byThermostat[key] = { opt, quantity: 0 };
      byThermostat[key].quantity += z.thermostatQuantity;
    });
    Object.keys(byThermostat)
      .sort((a, b) => thermostatOptions.findIndex((o) => o.id === a) - thermostatOptions.findIndex((o) => o.id === b))
      .forEach((key, i) => {
        const x = byThermostat[key];
        materials.push({
          type: 'thermostat', productId: 'floor-thermostat-' + key,
          variantId: (x.opt && x.opt.variantId) || null,
          title: (x.opt && x.opt.title) || 'Termostato AISEN FLOOR',
          imageUrl: (x.opt && x.opt.image) || null,
          description: 'Controle de temperatura da zona',
          specification: cfg.thermostatMaximumCurrent + ' A — ' + cfg.voltage + ' V',
          quantity: x.quantity, unit: 'un.', totalLabel: x.quantity + ' un.', sortOrder: 200 + i,
        });
      });

    // 3/4) demais auxiliares
    const boxQty = valid.reduce((s, z) => s + z.europeanBoxQuantity, 0);
    const contactorQty = valid.reduce((s, z) => s + z.contactorQuantity, 0);
    if (boxQty > 0) materials.push({
      type: 'box', productId: 'european-box-86', variantId: auxVariantId(cat.europeanBox), imageUrl: auxImage(cat.europeanBox),
      title: 'Caixa padrão europeu 86 × 86 mm', description: 'Caixa para embutir o termostato',
      specification: '86 × 86 mm',
      quantity: boxQty, unit: 'un.', totalLabel: boxQty + ' un.', sortOrder: 300,
    });
    if (contactorQty > 0) materials.push({
      type: 'contactor', productId: 'weg-contactor-25a', variantId: auxVariantId(cat.contactor), imageUrl: auxImage(cat.contactor),
      title: 'Contatora WEG ' + cfg.contactorOperationalCurrent + ' A', description: 'Acionamento de carga de aquecimento',
      specification: 'Bobina ' + cfg.voltage + ' VCA',
      quantity: contactorQty, unit: 'un.', totalLabel: contactorQty + ' un.', sortOrder: 400,
    });

    // 5) fita metálica — soma das unidades por zona (sem reaproveitar sobra entre ambientes)
    const tapeCfg = cfg.metallicTape || {};
    const tapeQty = valid.reduce((s, z) => s + ((z.metallicTape && z.metallicTape.quantity) || 0), 0);
    if (tapeCfg.enabled && tapeQty > 0) materials.push({
      type: 'tape', productId: 'floor-metallic-tape', variantId: auxVariantId(cat.metallicTape), imageUrl: auxImage(cat.metallicTape),
      title: 'Fita metálica AISEN FLOOR — ' + tapeCfg.unitLengthMeters + ' m',
      description: 'Fixação dos cabos de aquecimento',
      specification: tapeCfg.unitLengthMeters + ' m por unidade',
      quantity: tapeQty, unit: 'un.',
      totalLabel: (tapeQty * tapeCfg.unitLengthMeters) + ' m',
      totalLengthMeters: 0, // não entra no comprimento de cabo
      sortOrder: 500,
    });

    materials.sort((a, b) => a.sortOrder - b.sortOrder);

    const totalItemUnits = materials.reduce((s, m) => s + m.quantity, 0);
    const totalCableLength = materials.filter((m) => m.type === 'cable').reduce((s, m) => s + m.totalLengthMeters, 0);
    const missingVariants = materials.filter((m) => !m.variantId).map((m) => m.title);

    return { materials, totalItemUnits, totalCableLength, missingVariants, isComplete: missingVariants.length === 0 };
  }

  /* ---------------- carrinho ---------------- */

  // Uma linha por (zona × item) para não perder a relação produto ↔ ambiente.
  // As quantidades somadas por produto batem exatamente com a tabela consolidada.
  // Se faltar qualquer variante exigida pelo projeto, nada é enviado (composição incompleta é bloqueada).
  function buildFloorCartLines(project, catalog, options) {
    const cat = catalog || {};
    const lines = [];
    const consolidated = buildConsolidatedProjectMaterials(project, cat, options);

    if (!consolidated.isComplete) {
      return { lines: [], missing: consolidated.missingVariants, blocked: true, consolidated };
    }

    project.zones.filter((z) => z.status === STATUS.VALID).forEach((z) => {
      const props = {
        _aisen_floor_project_id: project.projectId,
        _aisen_floor_zone_id: z.zoneId,
        'Ambiente': z.environmentName,
        'Área aquecida': z.heatedArea.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) + ' m²',
        'Potência da zona': z.installedPower + ' W',
        'Potência efetiva': Math.round(z.installedWattsPerSquareMeter * 10) / 10 + ' W/m²',
        'Controle': z.controlType === CONTROL.CONTACTOR ? 'Termostato + contatora' : 'Acionamento direto',
        _aisen_floor_selection_type: z.selectionType || SELECTION.STANDARD,
      };
      if (z.lengthMeters && z.widthMeters) {
        props['Dimensões'] = z.lengthMeters.toLocaleString('pt-BR') + ' × ' + z.widthMeters.toLocaleString('pt-BR') + ' m';
      }
      const push = (variantId, quantity, extraProps) => {
        if (!quantity || !variantId) return;
        lines.push({ id: variantId, quantity, properties: Object.assign({}, props, extraProps || {}) });
      };
      z.selectedRolls.forEach((r) => push(r.variantId, r.quantity));
      const th = resolveThermostat(cat, z.thermostatId);
      push(th && th.variantId, z.thermostatQuantity, th ? { 'Termostato': th.title } : null);
      push(auxVariantId(cat.europeanBox), z.europeanBoxQuantity);
      push(auxVariantId(cat.contactor), z.contactorQuantity);
      const tp = z.metallicTape;
      if (tp && tp.enabled && tp.quantity > 0) {
        push(auxVariantId(cat.metallicTape), tp.quantity, { 'Fitas metálicas': tp.quantity + ' unidades de ' + tp.unitLengthMeters + ' m' });
      }
    });

    return { lines, missing: [], blocked: false, consolidated };
  }

  window.AisenFloorEngine = {
    AISEN_FLOOR_CONFIG, AISEN_FLOOR_ROLLS, STATUS, CONTROL, SELECTION,
    getSmallAreaFallbackMaximum, getSmallAreaFallbackRoll,
    getThermostatOptions, resolveThermostat, auxVariantId, auxImage,
    normalizeAreaInput, validateEnvironment, calculateMetallicTape,
    generateValidRollCombinations, scoreCombination, findBestRollCombination,
    calculateElectricalControl,
    calculateFloorZone, calculateFloorProject, consolidateFloorProject,
    buildConsolidatedProjectMaterials, buildFloorCartLines,
  };
})();
